import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorState } from "@/ui";

interface Props {
  children: ReactNode;
  /** Optional custom fallback; defaults to ErrorState with a reset action. */
  fallback?: (error: Error, reset: () => void) => ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Global ErrorBoundary — catches render-time crashes so one feature's bug never blanks the whole
 * app. Wraps the routed content; feature screens can also nest their own. Logs to console (the
 * packaged build wires a real reporter here).
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  private reset = (): void => this.setState({ error: null });

  override render(): ReactNode {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.reset);
      return (
        <ErrorState
          title="Une erreur est survenue"
          message={error.message || "L'écran a rencontré un problème inattendu."}
          onRetry={this.reset}
        />
      );
    }
    return this.props.children;
  }
}
